import * as React from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Award, Braces, Hash, ShieldCheck, Sparkles, type LucideIcon } from 'lucide-react'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'

/**
 * The badge a person earns by completing a program (0066).
 *
 * One SVG, drawn the same in light and dark mode on purpose: it is an object
 * people screenshot and share, so it must not change colour with the viewer's
 * theme. Navy ground, one brand accent per program, and the QR on a white
 * square — a QR needs a light quiet zone to scan, whatever sits around it.
 *
 * The QR opens /badges/<badge_id>, which is public and asks the database
 * whether the record is still complete. So a badge proves itself: a printed one
 * whose approval was later withdrawn scans to "could not verify".
 *
 * Colours are literal rather than tokens for the same reason as the theme
 * note above. #6cb144 / #becd33 / #8ecb69 are DPL palette greens, all light
 * enough to carry the navy icon (the same pairing index.css uses for
 * --brand / --brand-ink).
 */

const NAVY = '#182239'
const INK_MUTED = '#a9b4c2'

interface Look {
  icon: LucideIcon
  accent: string
}

/** A program's graphic. Unknown slugs get the generic award, never nothing. */
const LOOKS: Record<string, Look> = {
  'ai-enabled-professional': { icon: Sparkles, accent: '#8ecb69' },
  'ai-native-js-ts': { icon: Braces, accent: '#6cb144' },
  'ai-native-csharp-dotnet': { icon: Hash, accent: '#6cb144' },
  'engineer-yearly-training': { icon: ShieldCheck, accent: '#becd33' },
}
const FALLBACK: Look = { icon: Award, accent: '#6cb144' }

/**
 * The public verification URL a badge's QR code points at.
 *
 * Built from VITE_PUBLIC_ORIGIN when the deploy sets it (DPH_TENANT_URL), not
 * from wherever the badge happens to be rendered: a badge is screenshotted and
 * printed, and one drawn on the dev server or a LAN IP would encode a URL that
 * never verifies. The page's own origin is the fallback for local work.
 */
export function badgeUrl(badgeId: string): string {
  const origin = (import.meta.env.VITE_PUBLIC_ORIGIN || window.location.origin).replace(/\/+$/, '')
  return `${origin}/badges/${badgeId}`
}

/**
 * Greedy word wrap for SVG text, which does not wrap on its own. Two lines at
 * most; every program title today fits in two at this size.
 */
function wrap(text: string, max = 18): string[] {
  const lines: string[] = []
  let line = ''
  for (const word of text.split(/\s+/)) {
    if (line && (line + ' ' + word).length > max) {
      lines.push(line)
      line = word
    } else {
      line = line ? `${line} ${word}` : word
    }
  }
  if (line) lines.push(line)
  return lines.length > 2 ? [lines[0], lines.slice(1).join(' ')] : lines
}

export function ProgramBadge({
  title,
  slug,
  completedAt,
  badgeId,
  className,
}: {
  title: string
  slug: string
  completedAt: string | null
  badgeId: string
  className?: string
}) {
  const { icon: Icon, accent } = LOOKS[slug] ?? FALLBACK
  const lines = wrap(title)
  const date = formatDate(completedAt)
  const clipId = `badge-${React.useId().replace(/:/g, '')}`

  return (
    <svg
      viewBox="0 0 400 240"
      role="img"
      aria-label={`${title} — completed ${date}`}
      className={cn('h-auto w-full max-w-[400px] select-none', className)}
      style={{ fontFamily: 'Outfit, ui-sans-serif, system-ui, sans-serif' }}
    >
      {/* Everything decorative is clipped to the card, or the rings and the top
          band spill past the rounded corners. useId keeps the clip id unique
          when a page renders several badges (the status page renders dozens);
          its colons are stripped because they are not safe inside url(#…). */}
      <defs>
        <clipPath id={clipId}>
          <rect width="400" height="240" rx="16" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <rect width="400" height="240" fill={NAVY} />
        {/* Concentric rings behind the medallion: the seal. */}
        {[78, 96, 114].map((r) => (
          <circle key={r} cx="72" cy="88" r={r} fill="none" stroke={accent} strokeOpacity={0.12} />
        ))}
        <rect width="400" height="6" fill={accent} />
      </g>

      <circle cx="72" cy="88" r="44" fill={accent} />
      <circle cx="72" cy="88" r="38" fill="none" stroke={NAVY} strokeOpacity={0.35} strokeWidth="1.5" />
      <Icon x={50} y={66} size={44} color={NAVY} strokeWidth={1.75} />

      <text x="136" y="50" fill={accent} fontSize="11" fontWeight="600" letterSpacing="2">
        DON&#8217;T PANIC LABS
      </text>
      {lines.map((l, i) => (
        <text key={i} x="136" y={80 + i * 26} fill="#ffffff" fontSize="22" fontWeight="700">
          {l}
        </text>
      ))}
      <text x="136" y={80 + lines.length * 26} fill={INK_MUTED} fontSize="11" fontWeight="500" letterSpacing="1.5">
        PROGRAM COMPLETE
      </text>

      <text x="24" y="196" fill={INK_MUTED} fontSize="10" fontWeight="500" letterSpacing="1.5">
        COMPLETED
      </text>
      <text x="24" y="216" fill="#ffffff" fontSize="16" fontWeight="600">
        {date}
      </text>

      {/* Bottom right, on white: the quiet zone is what makes it scan. */}
      <rect x="300" y="140" width="84" height="84" rx="8" fill="#ffffff" />
      <QRCodeSVG
        value={badgeUrl(badgeId)}
        x={308}
        y={148}
        size={68}
        level="M"
        marginSize={0}
        bgColor="#ffffff"
        fgColor={NAVY}
      />
    </svg>
  )
}
